/**
 * The Traffic page's filter — Source → Campaign → Content — and the options
 * for each level, from /traffic/sub-source-funnel rows.
 *
 * Campaign and content are the source tree's own levels (lib/sourceTree), and
 * the backend reads them the same way (services/trafficQueries/scope.js):
 *
 *   source               campaign                     content
 *   everflow / katalys   affiliate id (sub_source)    sub1, or "(not set)"
 *   everything else      utm_campaign, else the       the sub_source (utm_content)
 *                        sub_source on its own         when a utm_campaign sits above it
 *
 * Options are grouped by their DECODED label, the way the tree shows them, and
 * each carries every raw spelling behind it ("a+b" and "a%20b" are one label),
 * because the server matches the raw values the URLs carried.
 */
import { decodeUTM } from './sourceTree'

export const AFFILIATE_SOURCES = ['everflow', 'katalys']
/** The content value the server reads as "no sub1" — the tree's "(not set)". */
export const NOT_SET = '__not_set__'
export const NOT_SET_LABEL = '(not set)'

const isAffiliate = source => AFFILIATE_SOURCES.includes(source)

// The route decodes sub_source / sub1 / utm_campaign for display and sends the
// URL's own spelling beside each as raw_*. Only the raw one matches server-side.
const raw = (row, key) => (`raw_${key}` in row ? row[`raw_${key}`] : row[key])

/** A row's raw campaign. */
export function campaignRawOf(row) {
  if (isAffiliate(row.source)) return raw(row, 'sub_source') ?? null
  return raw(row, 'utm_campaign') || raw(row, 'sub_source') || null
}

/** A row's raw content, NOT_SET for an affiliate row with no sub1, or null where the tree has no content level. */
export function contentRawOf(row) {
  if (isAffiliate(row.source)) return raw(row, 'sub1') || NOT_SET
  const campaign = raw(row, 'utm_campaign')
  const subSource = raw(row, 'sub_source')
  return campaign && campaign !== subSource ? subSource : null
}

const labelOf = raw => (raw === NOT_SET ? NOT_SET_LABEL : decodeUTM(raw))

function grouped(entries) {
  const byLabel = new Map()
  for (const { raw, visitors } of entries) {
    const label = labelOf(raw)
    if (!byLabel.has(label)) byLabel.set(label, { label, raws: new Set(), visitors: 0 })
    const option = byLabel.get(label)
    option.raws.add(raw)
    option.visitors += visitors
  }
  return [...byLabel.values()]
    .map(option => ({ ...option, raws: [...option.raws] }))
    .sort((a, b) => b.visitors - a.visitors || a.label.localeCompare(b.label))
}

/**
 * Campaign options for the chosen sources, and content options under each
 * campaign label. A label shared by two sources is one option.
 *
 * @param {object[]} rows  sub-source rows
 * @param {string[]} sources  chosen source ids; none means no options
 * @returns {{campaigns: {label: string, raws: string[], visitors: number}[], contents: Map<string, {label: string, raws: string[], visitors: number}[]>}}
 */
export function scopeOptions(rows, sources = []) {
  const mine = (rows || []).filter(row => sources.includes(row.source))
  const campaigns = grouped(mine
    .map(row => ({ raw: campaignRawOf(row), visitors: row.visitors || 0 }))
    .filter(entry => entry.raw))
  const contents = new Map()
  for (const campaign of campaigns) {
    const under = mine.filter(row => campaign.raws.includes(campaignRawOf(row)))
    const options = grouped(under
      .map(row => ({ raw: contentRawOf(row), visitors: row.visitors || 0 }))
      .filter(entry => entry.raw))
    if (options.length) contents.set(campaign.label, options)
  }
  return { campaigns, contents }
}

/** Content options under the chosen campaign labels, merged by label. */
export function contentOptionsFor(options, campaigns = []) {
  const byLabel = new Map()
  for (const campaign of campaigns) {
    for (const option of options?.contents?.get(campaign) || []) {
      const merged = byLabel.get(option.label) || { label: option.label, raws: [], visitors: 0 }
      merged.raws = [...new Set([...merged.raws, ...option.raws])]
      merged.visitors += option.visitors
      byLabel.set(option.label, merged)
    }
  }
  return [...byLabel.values()].sort((a, b) => b.visitors - a.visitors || a.label.localeCompare(b.label))
}

const rawsOf = (list, labels) => [...new Set(labels.flatMap(label => list.find(option => option.label === label)?.raws || []))]

/**
 * Query params for the chosen filter: `source`, and the raw `campaign` /
 * `content` values behind the chosen labels — arrays, which axios sends as
 * source[]=…. Empty for all traffic. Labels no longer on offer (another
 * source's campaign) are dropped rather than sent.
 */
export function scopeParams({ sources = [], campaigns = [], contents = [] }, options) {
  if (!sources.length) return {}
  const params = { source: sources }
  const campaign = rawsOf(options?.campaigns || [], campaigns)
  if (!campaign.length) return params
  params.campaign = campaign
  const content = rawsOf(contentOptionsFor(options, campaigns), contents)
  if (content.length) params.content = content
  return params
}

const summarize = (labels, noun) => (labels.length === 1 ? labels[0] : `${labels.length} ${noun}`)

/** "Meta, Google → Spring sale → 2 ads", for headings that say what is filtered. */
export function scopeLabel({ sources = [], campaigns = [], contents = [] }, sourceName) {
  if (!sources.length) return null
  return [
    sources.map(sourceName).join(', '),
    campaigns.length && summarize(campaigns, 'campaigns'),
    campaigns.length && contents.length && summarize(contents, 'content'),
  ].filter(Boolean).join(' → ')
}
